import { EventBus } from "@/event-bus";
import { Location } from "@/interfaces/location";
import { Policy, Role, Service } from "@/interfaces/roles";
import { User } from "@/interfaces/user";
import { locationsService } from "@/services/locations.service";
import { permissionRoleService } from "@/services/permissions.service";
import { Callback } from "@/types/types";
import PermissionModule from "@/vuex/modules/permission/permission.index";
import { PageTitleComponent } from "helix-vue-components";
import each from "lodash/each";
import find from "lodash/find";
import { Component, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { RolesModel } from "./declarations";
import NameAppsComponent from "./nameApps/nameApps.component";
import { PermissionLocationComponent } from "./permissionLocation/permissionLocation.component";
import formattedPolicies from "./permissionLocation/util";
import Template from "./roles.template.vue";
import { UsersComponent } from "./users/users.component";

@Component({
  mixins: [Template],
  components: {
    PageTitleComponent
  }
})
export default class RolesComponent extends Vue {
  @Action("getAllPolicies", { namespace: "PermissionModule" })
  public loadPolicies!: Callback;
  @Getter("policies", { namespace: "PermissionModule" })
  public policies!: Policy[];
  @Getter("services", { namespace: "authModule" })
  public services!: Service[];
  public loading = false;
  public finishLoading = false;
  public locations: Location[] = [];
  public fetchedRole: null | Role = null;
  public currentStep = 1;
  public rolePolicies: Policy[] = [];
  public dbUsers: User[] = [];
  public title = "role_mgmt.default_title";
  public model: RolesModel = {
    nameApps: {
      name: null,
      description: null,
      selectedLocations: [],
      selectedServices: []
    },
    permissionLocation: {
      policies: {}
    },
    usersForm: {
      users: []
    }
  };

  get stepsConfig() {
    return [
      {
        title: "role_mgmt.name_apps",
        component: NameAppsComponent,
        modelField: "nameApps",
        props: {
          services: this.services,
          locations: this.locations,
          loading: this.loading,
          policies: this.policies
        }
      },
      {
        title: "role_mgmt.permission_location",
        component: PermissionLocationComponent,
        modelField: "permissionLocation",
        props: {
          locations: this.model.nameApps.selectedLocations,
          services: this.model.nameApps.selectedServices,
          policies: this.policies,
          rolePolicies: this.rolePolicies
        }
      },
      {
        title: "role_mgmt.users",
        component: UsersComponent,
        modelField: "usersForm",
        props: {
          dataBaseUsers: this.dbUsers,
          loading: this.finishLoading
        }
      }
    ];
  }

  public stepChanged(step: number) {
    EventBus.$emit("rolesStepChanged", step);
  }

  public cancel() {
    this.$router.back();
  }

  public changeStep(step: number) {
    this.currentStep = step;
  }

  public async finish() {
    this.loading = true;
    this.finishLoading = true;
    await permissionRoleService.saveRole(
      {
        id: (this.fetchedRole && this.fetchedRole!.id) || undefined,
        name: this.model.nameApps.name!,
        desc: this.model.nameApps.description,
        policies: formattedPolicies(
          this.model.permissionLocation.policies,
          this.model.nameApps.selectedLocations
        ),
        users: this.model.usersForm.users
      },
      this.fetchedRole
    );
    this.loading = false;
    this.finishLoading = false;
  }

  protected getRoleLocations(policies: Policy[]): Location[] {
    const locations: Location[] = [];
    each(policies, (p: Policy) => {
      if (locations.length === this.locations.length) {
        return false;
      }
      if (p.options) {
        p.options.forEach(option => {
          if (
            option.location_id &&
            !find(locations, ["id", option.location_id])
          ) {
            const location = find(this.locations, ["id", option.location_id]);
            if (location) {
              locations.push(location);
            }
          }
        });
      }
    });
    return locations;
  }

  protected async mounted() {
    this.loading = true;
    this.locations = await locationsService.getAll();
    const roleId = +this.$route.params.id;
    if (roleId) {
      const [roleData, rolePolicies] = await Promise.all([
        permissionRoleService.getRole(roleId),
        permissionRoleService.getRolePolicies(roleId)
      ]);
      this.fetchedRole = {
        ...roleData,
        policies: rolePolicies.map((p: Policy) => {
          const { id, options } = p;
          return options ? { id, options } : { id };
        })
      };
      this.model.nameApps.description = roleData.desc;
      this.model.nameApps.name = roleData.name;
      this.model.nameApps.selectedServices = this.services.filter(
        s => !!find(rolePolicies, ["tag", s.id])
      );
      this.model.usersForm.users = this.dbUsers = roleData.users;
      this.rolePolicies = rolePolicies;
      this.model.nameApps.selectedLocations = this.getRoleLocations(
        rolePolicies
      );

      this.title = this.$t("role_mgmt.edit_title", {
        roleName: this.fetchedRole!.name
      }).toString();
    }
    await this.loadPolicies();
    this.loading = false;
  }

  protected beforeCreate() {
    this.$store.registerModule("PermissionModule", PermissionModule);
  }

  protected beforeDestroy() {
    this.$store.unregisterModule("PermissionModule");
  }
}
